import React from 'react';

export default function ProjectNameInput({ value, onChange, placeholder, label, disabled }) {
  const recognitionRef = React.useRef(null);
  const [recording, setRecording] = React.useState(false);
  const SpeechRecognition = React.useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    return window.SpeechRecognition || window.webkitSpeechRecognition;
  }, []);
  const speechSupported = !!SpeechRecognition;

  const stopRecognition = React.useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {}
    recognitionRef.current = null;
    setRecording(false);
  }, []);

  const startRecognition = React.useCallback(() => {
    if (!speechSupported || recording || disabled) return;
    try {
      const instance = new SpeechRecognition();
      instance.interimResults = true;
      instance.lang = 'en-US';
      instance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((result) => result[0]?.transcript || '')
          .join(' ')
          .trim();
        if (transcript && typeof onChange === 'function') {
          onChange(transcript);
        }
      };
      instance.onerror = () => stopRecognition();
      instance.onend = () => stopRecognition();
      recognitionRef.current = instance;
      instance.start();
      setRecording(true);
    } catch {
      stopRecognition();
    }
  }, [SpeechRecognition, speechSupported, recording, disabled, onChange, stopRecognition]);

  React.useEffect(() => () => stopRecognition(), [stopRecognition]);

  const handleToggleRecording = React.useCallback(() => {
    if (!speechSupported) return;
    if (recording) {
      stopRecognition();
    } else {
      startRecognition();
    }
  }, [speechSupported, recording, startRecognition, stopRecognition]);

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input
        aria-label={label || 'Project name'}
        placeholder={placeholder || 'Project name'}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        disabled={disabled}
        style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}
      />
      <button
        aria-label={recording ? 'Stop recording project name' : 'Record project name'}
        onClick={handleToggleRecording}
        disabled={!speechSupported || disabled}
        style={{
          padding: '0 12px',
          borderRadius: 8,
          border: '1px solid #374151',
          background: recording ? '#ef4444' : '#00BBF9',
          color: recording ? '#fff' : '#0b0b0e',
          fontWeight: 600,
          cursor: (!speechSupported || disabled) ? 'not-allowed' : 'pointer'
        }}
      >
        {speechSupported ? (recording ? 'Stop' : 'Mic') : 'Mic'}
      </button>
    </div>
  );
}
